import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function TermsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service',
    description: 'Terms of service for Fresno Electrical Services',
    url: `${siteUrl}/terms`
  };

  return (
    <>
      <SEOHead
        title="Terms of Service | Fresno Electrical Services"
        description="Terms of service for Fresno Electrical Services. Read our terms and conditions for using our electrical services."
        canonical="/terms"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20 border-t-4 border-yellow-500">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/2dca6e5e592f27df9764800067fa82aa.jpg')] opacity-5 bg-cover bg-center"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-gray-300">Last Updated: January 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 py-16 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Electrician Solutions Fresno ("Company," "we," "our," or "us") regarding your use of our website and electrical services in Fresno, California and surrounding areas.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing our website or engaging our services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you may not use our services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Services Provided</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Electrician Solutions Fresno is a licensed C-10 electrical contractor providing professional electrical services including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Residential electrical installations, repairs, and maintenance</li>
                <li>Commercial electrical specialist services</li>
                <li>Smart home and energy management systems</li>
                <li>Lighting design and outdoor lighting installation</li>
                <li>Renewable energy solutions and EV charging stations</li>
                <li>Electrical testing and certification</li>
                <li>Fire safety and alarm systems</li>
                <li>24/7 emergency electrical repairs</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Service Agreements</h2>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">Quotes and Estimates</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                All quotes and estimates provided are valid for 30 days from the date of issue unless otherwise specified. Quotes are based on the information provided by the customer and may be subject to change upon inspection of the actual work site.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">Scheduling and Appointments</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Service appointments must be scheduled in advance. We offer 24/7 emergency services for urgent electrical issues. Cancellations or rescheduling requests must be made at least 24 hours in advance to avoid cancellation fees.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">Scope of Work</h3>
              <p className="text-gray-700 leading-relaxed">
                The scope of work will be clearly defined in writing before services begin. Any changes or additions to the agreed scope may result in additional charges and will require customer approval.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Terms</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Payment is due upon completion of services unless otherwise agreed in writing</li>
                <li>We accept cash, check, credit cards, and electronic payments</li>
                <li>For large projects, a deposit may be required before work begins</li>
                <li>Late payments may incur interest charges at the rate of 1.5% per month</li>
                <li>Returned checks will incur a $35 processing fee</li>
                <li>Unpaid invoices may be subject to collection proceedings</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Customer Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a customer, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide accurate information about the electrical work needed</li>
                <li>Ensure safe and clear access to work areas</li>
                <li>Secure pets and remove obstacles that may interfere with work</li>
                <li>Obtain any necessary permissions from landlords or property managers</li>
                <li>Be present or designate an authorized representative during service</li>
                <li>Notify us of any known hazards or special conditions at the work site</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Warranties and Guarantees</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We stand behind our work with the following warranties:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>All labor is guaranteed for 1 year from the date of service completion</li>
                <li>Parts and materials carry manufacturer warranties as applicable</li>
                <li>Warranty does not cover damage caused by misuse, neglect, or unauthorized modifications</li>
                <li>Emergency repairs may have different warranty terms as specified in writing</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                To make a warranty claim, contact us within the warranty period with proof of service.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Our liability is limited to the amount paid for the specific service in question</li>
                <li>We are not liable for indirect, incidental, or consequential damages</li>
                <li>We are not responsible for pre-existing conditions or code violations not identified during our inspection</li>
                <li>We are not liable for delays caused by factors beyond our control (weather, supply chain issues, permit delays)</li>
                <li>Our liability does not extend to work performed by other contractors or DIY modifications</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Licensing and Compliance</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Electrician Solutions Fresno is a licensed C-10 electrical contractor in the State of California. All work is performed in compliance with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>California Electrical Code</li>
                <li>National Electrical Code (NEC)</li>
                <li>Local building codes and ordinances in Fresno and surrounding areas</li>
                <li>OSHA safety standards</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Permits will be obtained as required by local authorities. Permit fees are the responsibility of the customer unless otherwise agreed.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Insurance and Indemnification</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We maintain appropriate liability insurance and workers' compensation coverage. Customers agree to indemnify and hold harmless Electrician Solutions Fresno from any claims arising from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Customer-provided materials or equipment</li>
                <li>Failure to disclose known hazards or conditions</li>
                <li>Unauthorized modifications to our completed work</li>
                <li>Third-party claims related to customer property</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on our website, including text, graphics, logos, images, and software, is the property of Electrician Solutions Fresno and is protected by copyright and trademark laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                In the event of a dispute:
              </p>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2">
                <li>Contact us directly to attempt informal resolution</li>
                <li>If informal resolution fails, disputes may be submitted to mediation</li>
                <li>Any legal action must be brought in the courts of Fresno County, California</li>
                <li>California law governs these Terms without regard to conflict of law principles</li>
              </ol>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Emergency Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our 24/7 emergency services are available for urgent electrical issues that pose immediate safety risks. Emergency service rates apply outside of normal business hours. What constitutes an emergency is determined at our discretion based on safety considerations.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                Either party may terminate a service agreement with written notice. If you terminate after work has begun, you are responsible for payment for all work completed and materials purchased. We reserve the right to terminate service if safety concerns arise or if payment terms are not met.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 border-l-4 border-yellow-500 p-6 rounded">
                <p className="text-gray-900 font-semibold mb-2">Electrician Solutions Fresno</p>
                <p className="text-gray-700">Fresno, California</p>
                <p className="text-gray-700">Phone: <a href="tel:+15595551234" className="text-yellow-600 hover:text-yellow-700 no-underline">(559) 555-1234</a></p>
                <p className="text-gray-700">Email: <a href="mailto:info@electriciansolutionsfresno.com" className="text-yellow-600 hover:text-yellow-700 no-underline">info@electriciansolutionsfresno.com</a></p>
                <p className="text-gray-700 mt-2 text-sm">Licensed C-10 Electrical Contractor</p>
                <p className="text-gray-700 text-sm">Serving Fresno, Clovis, Madera, and surrounding areas</p>
              </div>
            </section>

            <section>
              <p className="text-gray-600 text-sm italic">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>

          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
